import { useMutation } from '@apollo/client';
import { useIsBroadbeanEnabled } from 'hooks/company';
import { useSnackbar } from 'notistack';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import ShareButton from 'screens/company-user/job/job-v1/title-row/ShareButton';

import EditIcon from '@mui/icons-material/Edit';
import SendIcon from '@mui/icons-material/Send';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  CircularProgress,
} from '@mui/material';
import { makeStyles } from '@mui/styles';

import {
  BroadbeanJobStatusEnum,
  Job,
  useSubmitJobToBroadbeanMutation,
} from '@libs/graphql-types';

import { UPDATE_JOB } from '../../../../job-form/queries';
import { JobEditPopup } from './JobEditPopup';
import { PerformanceMetrics } from './PerformanceMetrics';

const useStyles = makeStyles((theme: any) => ({
  root: {
    marginBottom: theme.spacing(3),
    backgroundColor: 'transparent',
    boxShadow: 'none',
  },
  header: {
    padding: theme.spacing(0),
  },
  titleContainer: {
    marginBottom: theme.spacing(6),
  },
  title: {
    fontSize: '1.5rem',
    lineHeight: '1.75rem',
    fontWeight: 500,
    color: theme.palette.text.primary,

    [theme.breakpoints.down('md')]: {
      marginBottom: theme.spacing(4),
    },
  },
  actionsContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing(1),

    [theme.breakpoints.down('sm')]: {
      justifyContent: 'center',
      gap: theme.spacing(2),
      marginTop: theme.spacing(2),
    },
  },
  actionButton: {
    textTransform: 'uppercase',
    minWidth: 'unset',
    padding: theme.spacing(1, 2),
    [theme.breakpoints.down('sm')]: {
      flex: '1 1 50%',
      fontSize: '0.875rem',
    },
    '& .MuiButton-startIcon': {
      margin: 0,
      marginRight: theme.spacing(1),
      [theme.breakpoints.down('sm')]: {
        marginRight: theme.spacing(0.5),
      },
    },
  },
  actionIcon: {
    fontSize: '1.25rem',
    [theme.breakpoints.down('sm')]: {
      fontSize: '1rem',
    },
  },
  statusChip: {
    marginLeft: theme.spacing(2),
  },
  shareButton: {
    textTransform: 'uppercase',
    [theme.breakpoints.down('sm')]: {
      flex: '1 1 50%',
      fontSize: '0.875rem',
      '& .MuiButton-endIcon': {
        marginLeft: theme.spacing(0.5),
      },
    },
  },
}));

interface JobHeaderProps {
  jobData?: Job;
  refetch?: () => void;
}

export const JobHeader: React.FC<JobHeaderProps> = ({ jobData, refetch }) => {
  const classes = useStyles();
  const { t } = useTranslation('job');
  const [editOpen, setEditOpen] = useState(false);
  const [updateJob] = useMutation(UPDATE_JOB);
  const [submitToBroadbean, { loading: broadbeanLoading }] =
    useSubmitJobToBroadbeanMutation();
  const { enqueueSnackbar } = useSnackbar();
  const isBroadbeanEnabled = useIsBroadbeanEnabled();

  if (!jobData) return null;

  const isPosted =
    jobData.broadbean_status === BroadbeanJobStatusEnum.Posted;

  const handleSubmitToBroadbean = async () => {
    try {
      const { data } = await submitToBroadbean({
        variables: { job_id: jobData.id },
      });
      const url = data?.submitJobToBroadbean?.broadbean_url;
      if (url) {
        window.open(url, '_blank');
      }
      if (refetch) await refetch();
    } catch {
      enqueueSnackbar(t('v2.broadbeanError', 'Failed to submit to Broadbean'), {
        variant: 'error',
      });
    }
  };

  const handleEdit = async (values: {
    description: string;
    campaign_owner_id: string;
  }) => {
    await updateJob({
      variables: {
        id: jobData.id,
        description: values.description,
        campaign_owner_id: values.campaign_owner_id,
        name: jobData.name,
      },
    });
    if (refetch) await refetch();

    enqueueSnackbar(t('v2.jobUpdated'), {
      variant: 'success',
    });
  };

  return (
    <Paper className={classes.root}>
      <Box className={classes.header}>
        <Grid container alignItems="center" className={classes.titleContainer}>
          <Grid item flexGrow={1}>
            <Typography className={classes.title}>
              {jobData.name || (jobData as any).title || ''}
            </Typography>
          </Grid>

          <Grid item xs={12} md="auto" container spacing={4}>
            {isBroadbeanEnabled && (
              <Grid item xs={6} md="auto">
                <Button
                  variant="outlined"
                  color="info"
                  fullWidth
                  className={classes.actionButton}
                  onClick={handleSubmitToBroadbean}
                  disabled={broadbeanLoading}
                  startIcon={
                    broadbeanLoading ? (
                      <CircularProgress size={18} />
                    ) : (
                      <SendIcon className={classes.actionIcon} />
                    )
                  }
                >
                  {isPosted
                    ? t('v2.editOnBroadbean', 'Edit on Broadbean')
                    : t('v2.postToBroadbean', 'Post to Broadbean')}
                </Button>
              </Grid>
            )}
            <Grid item xs={6} md="auto">
              <Button
                variant="outlined"
                color="info"
                fullWidth
                className={classes.actionButton}
                onClick={() => setEditOpen(true)}
                startIcon={<EditIcon className={classes.actionIcon} />}
              >
                {t('v2.edit')}
              </Button>
            </Grid>
            <Grid item xs={6} md="auto">
              <ShareButton
                job={jobData as Job}
                className={classes.shareButton}
              />
            </Grid>
          </Grid>
        </Grid>

        <PerformanceMetrics jobData={jobData} />
      </Box>
      <JobEditPopup
        open={editOpen}
        onClose={() => setEditOpen(false)}
        jobData={jobData}
        onSubmit={handleEdit}
      />
    </Paper>
  );
};
